import type { DialogProps } from '@ariakit/react/dialog'
import {
	type CSSProperties,
	forwardRef,
	type HTMLAttributes,
	type ImgHTMLAttributes
} from 'react'
import { ButtonLegacy, type ButtonProps } from '@/components/ui/button-legacy'
import { Dialog, DialogBody } from '@/components/ui/dialog-atoms'
import { Image01Icon } from '@/components/ui/icons'

// Flexbox-like props helper
interface FlexProps {
	direction?: 'row' | 'column'
	align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline'
	justify?: 'start' | 'center' | 'end' | 'space-between' | 'space-around'
	gap?: number
	fullWidth?: boolean
}

const getFlexClasses = ({
	direction,
	align,
	justify,
	fullWidth
}: FlexProps) => {
	const classes: string[] = ['flex']
	if (direction === 'column') {
		classes.push('flex-col')
	}
	if (direction === 'row') {
		classes.push('flex-row')
	}
	if (align === 'center') {
		classes.push('items-center')
	}
	if (align === 'start') {
		classes.push('items-start')
	}
	if (align === 'end') {
		classes.push('items-end')
	}
	if (align === 'stretch') {
		classes.push('items-stretch')
	}
	if (align === 'baseline') {
		classes.push('items-baseline')
	}
	if (justify === 'center') {
		classes.push('justify-center')
	}
	if (justify === 'start') {
		classes.push('justify-start')
	}
	if (justify === 'end') {
		classes.push('justify-end')
	}
	if (justify === 'space-between') {
		classes.push('justify-between')
	}
	if (justify === 'space-around') {
		classes.push('justify-around')
	}
	if (fullWidth) {
		classes.push('w-full')
	}
	return classes.join(' ')
}

export const OpsVettingSectionTitle = (
	props: HTMLAttributes<HTMLHeadingElement>
) => <h3 className='font-medium text-lg text-neutral-900' {...props} />

export const OpsVettingCardTitle = (
	props: HTMLAttributes<HTMLHeadingElement>
) => <h3 className='font-medium text-base text-neutral-900' {...props} />

export const OpsVettingLabel = (props: HTMLAttributes<HTMLSpanElement>) => (
	<span className='font-medium text-neutral-600 text-sm' {...props} />
)

export const OpsVettingParagraph = (
	props: HTMLAttributes<HTMLParagraphElement>
) => (
	<p
		className='break-words text-neutral-900 text-sm [&_strong]:font-medium'
		{...props}
	/>
)

export const OpsVettingDisabledContainer = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement> & { $isDisabled: boolean }
>(({ $isDisabled, className, ...props }, ref) => {
	return (
		<div
			className={`w-full ${$isDisabled ? 'pointer-events-none opacity-40' : ''} ${className ?? ''}`}
			{...props}
			ref={ref}
		/>
	)
})

export const OpsVettingHero = ({
	className,
	children,
	direction,
	align,
	justify,
	gap,
	fullWidth,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & FlexProps) => {
	const flexClasses = getFlexClasses({
		direction,
		align,
		justify,
		fullWidth
	})
	return (
		<div
			className={`${flexClasses} py-4 [&_h3]:font-medium [&_h3]:text-neutral-900 [&_h3]:text-xl ${className ?? ''}`}
			style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
			{...props}
		>
			{children}
		</div>
	)
}

export const OpsVettingQuestionsGrid = (
	props: HTMLAttributes<HTMLDivElement>
) => (
	<div
		className='grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-6'
		{...props}
	/>
)

export const OpsVettingSectionWrapper = ({
	className,
	direction,
	align,
	justify,
	gap,
	fullWidth,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & FlexProps) => {
	const flexClasses = getFlexClasses({
		direction,
		align,
		justify,
		fullWidth
	})
	return (
		<div
			className={`${flexClasses} py-6 ${className ?? ''}`}
			style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
			{...props}
		/>
	)
}

export const OpsVettingCardContainer = ({
	className,
	direction,
	align,
	justify,
	gap,
	fullWidth,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & FlexProps) => {
	const flexClasses = getFlexClasses({
		direction,
		align,
		justify,
		fullWidth
	})
	return (
		<div
			className={`${flexClasses} w-full rounded-2xl border border-neutral-300 p-4 ${className ?? ''}`}
			style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
			{...props}
		/>
	)
}

export const OpsVettingImage = (props: ImgHTMLAttributes<HTMLImageElement>) => (
	// biome-ignore lint/correctness/useImageSize: dimensions handled by CSS aspect-ratio and w-full
	<img
		className='aspect-square w-full rounded-lg object-cover'
		{...props}
		alt={props.alt}
	/>
)

export const OpsVettingImageTitle = (
	props: HTMLAttributes<HTMLHeadingElement>
) => (
	<h4
		className='w-full overflow-hidden text-ellipsis font-medium text-neutral-900 text-xs'
		{...props}
	/>
)

export const OpsVettingCardGrid = (props: HTMLAttributes<HTMLDivElement>) => (
	<div className='grid w-full grid-cols-1 xxl:grid-cols-2 gap-6' {...props} />
)

export const OpsVettingGalleryGrid = (
	props: HTMLAttributes<HTMLDivElement>
) => <div className='grid w-full grid-cols-4 xxl:grid-cols-5' {...props} />

export const OpsVettingCardBodyContainer = (
	props: HTMLAttributes<HTMLDivElement>
) => (
	<div
		className='grid w-full grid-cols-[minmax(0,1fr)_264px] justify-items-end gap-4'
		{...props}
	/>
)

export const OpsVettingGalleryItemsContainer = ({
	className,
	direction,
	align,
	justify,
	gap,
	fullWidth,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & FlexProps) => {
	const flexClasses = getFlexClasses({
		direction,
		align,
		justify,
		fullWidth
	})
	return (
		<div
			className={`${flexClasses} overflow-hidden p-5 ${className ?? ''}`}
			style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
			{...props}
		/>
	)
}

export const OpsVettingFlagsContainer = ({
	$flagPosition,
	className,
	gap,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & {
	$flagPosition: 'left' | 'right'
	gap?: number
}) => (
	<div
		className={`flex flex-wrap ${$flagPosition === 'left' ? 'flex-row-reverse' : 'flex-row'} ${className ?? ''}`}
		style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
		{...props}
	/>
)

export const OpsVettingImgPlaceHolder = (
	props: Omit<HTMLAttributes<HTMLDivElement>, 'children'>
) => (
	<div
		className='flex aspect-square w-full shrink-0 items-center justify-center rounded-lg bg-neutral-100 [&>svg]:text-neutral-400'
		{...props}
	>
		<Image01Icon />
	</div>
)

export const OpsVettingCheckIconContainer = (
	props: HTMLAttributes<HTMLDivElement>
) => (
	<div
		className='flex h-8 w-8 items-center justify-center rounded-2xl bg-neutral-white/90'
		{...props}
	/>
)

export const OpsVettingChoosePhotoDialog = ({
	width,
	style,
	...props
}: DialogProps & { width?: CSSProperties['width'] }) => (
	<Dialog
		className='grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-y-hidden'
		style={{ width, ...style }}
		{...props}
	/>
)

export const OpsVettingChoosePhotoDialogBody = ({
	gap = 6,
	className,
	...props
}: HTMLAttributes<HTMLDivElement> & { gap?: number }) => (
	<DialogBody
		className={`grid grid-cols-3 overflow-hidden ${className ?? ''}`}
		style={{ gap: `${gap * 4}px` }}
		{...props}
	/>
)

export const OpsVettingNoCoverPhotoContainer = ({
	className,
	direction,
	align,
	justify,
	gap,
	fullWidth,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & FlexProps) => {
	const flexClasses = getFlexClasses({
		direction,
		align,
		justify,
		fullWidth
	})
	return (
		<div
			className={`${flexClasses} aspect-square w-full rounded-lg bg-neutral-100 [&_h4]:font-medium [&_h4]:text-lg [&_h4]:text-neutral-900 [&_svg]:text-neutral-900 ${className ?? ''}`}
			style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
			{...props}
		/>
	)
}

export const OpsVettingRejectDialogBody = (
	props: HTMLAttributes<HTMLDivElement>
) => (
	<div
		className='grid h-full grid-cols-[200px_auto] grid-rows-[minmax(0,1fr)] gap-6 overflow-hidden p-6'
		{...props}
	/>
)

export const OpsVettingRejectDialogBodyFlagsItem = ({
	className,
	direction,
	align,
	justify,
	gap,
	fullWidth,
	style,
	...props
}: HTMLAttributes<HTMLDivElement> & FlexProps) => {
	const flexClasses = getFlexClasses({
		direction,
		align,
		justify,
		fullWidth
	})
	return (
		<div
			className={`${flexClasses} w-full break-words font-medium text-neutral-700 text-sm ${className ?? ''}`}
			style={{ gap: gap ? `${gap * 4}px` : undefined, ...style }}
			{...props}
		/>
	)
}

export const OpsVettingRejectDialog = ({
	width,
	style,
	...props
}: DialogProps & { width?: CSSProperties['width'] }) => (
	<Dialog
		className='grid grid-rows-[auto_minmax(0,1fr)_auto]'
		style={{ width, ...style }}
		{...props}
	/>
)

export const OpsVettingDragButton = ({
	$isDragging,
	style,
	...props
}: ButtonProps & { $isDragging: boolean }) => (
	<ButtonLegacy
		style={{ cursor: $isDragging ? 'grabbing' : 'grab', ...style }}
		{...props}
	/>
)
